import { FormGroup, Select, SubmitButton, Textarea } from "@xola/ui-kit";
import { FormField, InputField } from "common";
import { Field } from "formik";
import React from "react";
import { useMutation } from "react-query";
import { XolaBotPreference, XolaBotPreferenceType } from "types";
import { api } from "../../../api/api";
import { useSeller } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { ContentLayout } from "../../Layout/ContentLayout";

interface FormState {
    type: XolaBotPreferenceType;
    urls: string;
}

export const EditXolaBotPreferenceForm = () => {
    const seller = useSeller();
    const { xolabot: xolabotPreference } = seller.preferences;
    const invalidate = useInvalidate();

    const { mutateAsync: updatePreference, isLoading } = useMutation(async (payload: XolaBotPreference) => {
        await api.put(`/sellers/${seller.id}/preferences/xolabot`, payload);
        invalidate("sellers", seller.id);
    });

    const handleSubmit = (values: FormState) => {
        updatePreference({
            id: xolabotPreference.id,
            type: values.type,
            urls: values.type ? values.urls.split("\n").filter(Boolean) : [],
        });
    };

    const initialValues: FormState = {
        type: xolabotPreference.type ?? XolaBotPreferenceType.NONE,
        urls: xolabotPreference.urls.join("\n"),
    };

    return (
        <XolaFormik
            initialValues={initialValues}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, values, handleReset }) => (
                <ContentLayout isWide isDisabled={isLoading}>
                    <ContentLayout.Section
                        title="Webpage"
                        description="Configure URLs to either allow or block Xolabot from you webpages."
                    >
                        <FormField name="type" label="URL Shortlist">
                            <InputField as={Select}>
                                <option value={XolaBotPreferenceType.NONE}>Display across website</option>
                                <option value={XolaBotPreferenceType.WHITELIST}>Allowed URLs</option>
                                <option value={XolaBotPreferenceType.BLACKLIST}>Blocked URLs</option>
                            </InputField>
                        </FormField>

                        {values.type && (
                            <FormGroup>
                                <Field
                                    as={Textarea}
                                    className="mb-2 h-25"
                                    name="urls"
                                    placeholder="https//*.example.com/*"
                                />

                                <div className="text-gray-darker">
                                    Enter multiple URLs separated by a new line. Use wildcard character * in any part of
                                    the URL.
                                </div>
                            </FormGroup>
                        )}
                    </ContentLayout.Section>
                    <ContentLayout.Footer>
                        <SubmitButton type="button" isLoading={isLoading} onClick={handleSubmit}>
                            Save
                        </SubmitButton>

                        <SecondaryButton onClick={handleReset}>Cancel</SecondaryButton>
                    </ContentLayout.Footer>
                </ContentLayout>
            )}
        </XolaFormik>
    );
};
